<template>
  <div
    data-mouse
    data-mouse-attractionState="active"
    data-mouse-attraction="high"
    data-mouse-attractionVal="30"
    data-mouse-magnetism="5"
    data-hover-type="fadeout"
    @click="buttonClick"
  >
    <div :class="[played ? 'audio-button audio-button--active' : 'audio-button']">
      <span data-tline class="audio-button__line"></span>
      <span data-tline class="audio-button__line"></span>
      <span data-tline class="audio-button__line"></span>
    </div>
  </div>
</template>

<script>
import gsap from 'gsap';
import { mapGetters } from 'vuex';
import { Howl } from 'howler';

import Konami from '@/scripts/konami';
import { isMobile } from '@/scripts/utils';

export default {
  name: 'ToggleAudio',

  props: ['loaderIsOut'],

  computed: {
    ...mapGetters({
      videoIsPlaying: 'getStateVideo',
    }),
  },

  watch: {
    loaderIsOut(newVal) {
      if (newVal) this.enterAnimation();
    },

    videoIsPlaying(newVal) {
      if (this.$store.state.userStopAudio) return;

      if (newVal && this.played) {
        this.videoPlay();
      } else if (!newVal && !this.played) {
        this.videoPlay();
      }
    },
  },

  data() {
    return {
      played: true,
      scales: [0.4, 0.3, 0.4, 0.3],
      lines: [],
      isMobile,
    };
  },

  mounted() {
    if (!this.isMobile()) {
      const konami = new Konami();
      konami.init();

      window.addEventListener('konamiChange', this.konamiCode.bind(this));

      this.sound = new Howl({
        src: ['/static/audio/loop.webm', '/static/audio/loop.mp3'],
        autoplay: true,
        loop: true,
        volume: 0.1,
      });
    }
  },

  methods: {
    buttonClick() {
      this.played = !this.played;

      if (!this.played) {
        this.$store.commit('SET_STATEAUDIO', true);

        this.sound.pause();

        gsap.to(this.lines, 0.5, {
          scaleY: 0.3,
          ease: gsap.Power1.easeInOut,
        });
      } else {
        this.$store.commit('SET_STATEAUDIO', false);

        this.sound.play();
        this.sound.fade(0, 0.1, 1000);

        gsap.staggerTo(this.lines, 0.9, {
          scaleY: 1,
          repeat: -1,
          ease: gsap.Power1.easeInOut,
          yoyo: true,
        }, 0.3);
      }
    },

    videoPlay() {
      this.played = !this.played;

      if (!this.played) {
        this.sound.fade(0.1, 0, 1000);

        gsap.to(this.lines, 0.5, {
          scaleY: 0.3,
          ease: gsap.Power1.easeInOut,
        });
      } else {
        this.sound.fade(0, 0.1, 1000);

        gsap.staggerTo(this.lines, 0.9, {
          scaleY: 1,
          repeat: -1,
          ease: gsap.Power1.easeInOut,
          yoyo: true,
        }, 0.3);
      }
    },

    enterAnimation() {
      this.lines = Array.from(this.$el.querySelectorAll('[data-tline]'));

      gsap.staggerFromTo(this.lines, 0.9, {
        scaleY: 0,
      }, {
        scaleY: 1,
        ease: gsap.Power1.easeInOut,
      }, 0.3);
      gsap.staggerTo(this.lines, 0.9, {
        scaleY: i => this.scales[i],
        repeat: -1,
        ease: gsap.Power1.easeInOut,
        yoyo: true,
        delay: 0.9,
      }, 0.3);
    },

    konamiCode() {
      window.removeEventListener('konamiChange', this.konamiCode);
      this.gif = new Image();
      this.gif.src = '/static/img/extra/konami.gif';
      // this.gif.onload = (response) => {
      // };

      this.$parent.$parent.$el.appendChild(this.gif);

      gsap.set(this.gif, {
        position: 'fixed',
        zIndex: 30,
        bottom: 0,
        left: '50%',
        marginLeft: '-83px',
        yPercent: 100,
        autoAlpha: 0,
      });
      gsap.to(this.gif, 1.2, {
        autoAlpha: 1,
        yPercent: 0,
        ease: gsap.Power2.easeOut,
      });

      this.yodeling = new Howl({
        src: ['/static/audio/yodeling.webm', '/static/audio/yodeling.mp3'],
        volume: 0.1,
        autoplay: true,
        onload: () => {
          this.sound.fade(0.1, 0, 2000);
          this.sound.pause();
          this.yodeling.fade(0, 0.1, 2000);
        },
        onend: () => {
          this.sound.play();
          this.sound.fade(0, 0.1, 2000);
          this.yodeling.unload();

          gsap.to(this.gif, 0.6, {
            autoAlpha: 0,
            yPercent: 100,
            ease: gsap.Power2.easeOut,
            onComplete: () => {
              this.$parent.$parent.$el.removeChild(this.gif);
            },
          });
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../styles/colors';
@import '../application';


[data-mouse] {
  display: inline-block;
  position: absolute;
  opacity: 0;
  width: 0;
  overflow: hidden;
  cursor: pointer;

  @include mq(sm) {
    position: relative;
    opacity: 1;
    width: auto;
    overflow: visible;
  }

  @at-root .pointerevents:not(.touchevents) &:hover .audio-button .audio-button__line,
  .no-pointerevents.no-touchevents &:hover .audio-button .audio-button__line {
    background-color: $color-blue;
  }
}

.audio-button {
  position: relative;
  width: 14px;
  height: 16px;
  padding: 20px;
  box-sizing: content-box;

  display: flex;
  align-items: center;
  justify-content: space-between;

  cursor: pointer;

  &__line {
    display: inline-block;
    width: 1px;
    height: 100%;

    background-color: $color-light;
    transform-origin: 50% 100%;
    transition: background-color 0.3s cubic-bezier(0.215, 0.61, 0.355, 1);
  }
}
</style>



// WEBPACK FOOTER //
// src/components/ToggleAudio.vue